/**
 * Platform and browser detection utilities for PWA
 */

export interface PlatformInfo {
  isIOS: boolean;
  isAndroid: boolean;
  isMobile: boolean;
  isStandalone: boolean;
  browser: 'safari' | 'chrome' | 'firefox' | 'edge' | 'samsung' | 'other';
  supportsInstallPrompt: boolean;
}

export function getPlatformInfo(): PlatformInfo {
  const ua = navigator.userAgent.toLowerCase();
  const isStandalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone ===
      true;

  // Detect iOS
  const isIOS =
    /iphone|ipad|ipod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  // Detect Android
  const isAndroid = /android/.test(ua);

  // Detect mobile (general)
  const isMobile = isIOS || isAndroid || /mobile/.test(ua);

  // Detect browser
  let browser: PlatformInfo['browser'] = 'other';

  if (/safari/.test(ua) && !/chrome/.test(ua) && !/edg/.test(ua)) {
    browser = 'safari';
  } else if (/edg/.test(ua)) {
    browser = 'edge';
  } else if (/samsungbrowser/.test(ua)) {
    browser = 'samsung';
  } else if (/chrome/.test(ua)) {
    browser = 'chrome';
  } else if (/firefox/.test(ua)) {
    browser = 'firefox';
  }

  // Browsers that support beforeinstallprompt
  const supportsInstallPrompt =
    ['chrome', 'edge', 'samsung'].includes(browser) && !isIOS;

  return {
    isIOS,
    isAndroid,
    isMobile,
    isStandalone,
    browser,
    supportsInstallPrompt,
  };
}

export function getInstallInstructions(platform: PlatformInfo): {
  canShowCustomPrompt: boolean;
  title: string;
  instructions: string[];
  icon: string;
} {
  // iOS Safari - manual instructions
  if (platform.isIOS && platform.browser === 'safari') {
    return {
      canShowCustomPrompt: false,
      title: 'Install Polybank',
      instructions: [
        'Tap the Share button (square with arrow)',
        'Scroll down and tap "Add to Home Screen"',
        'Tap "Add" to install',
      ],
      icon: '📱',
    };
  }

  // iOS Chrome - needs Safari
  if (platform.isIOS && platform.browser === 'chrome') {
    return {
      canShowCustomPrompt: false,
      title: 'Install Polybank',
      instructions: [
        'Open this page in Safari browser',
        'Tap the Share button',
        'Select "Add to Home Screen"',
      ],
      icon: '🔄',
    };
  }

  // Android browsers with install prompt support
  if (platform.supportsInstallPrompt) {
    return {
      canShowCustomPrompt: true,
      title: 'Install Polybank',
      instructions: [
        'Tap "Install" below for quick access',
        'Or use the menu (⋮) → "Install app"',
      ],
      icon: '📲',
    };
  }

  // Android Firefox - manual instructions
  if (platform.isAndroid && platform.browser === 'firefox') {
    return {
      canShowCustomPrompt: false,
      title: 'Install Polybank',
      instructions: [
        'Tap the three dots menu (⋮)',
        'Select "Install"',
        'Confirm installation',
      ],
      icon: '📱',
    };
  }

  // Desktop fallback
  return {
    canShowCustomPrompt: platform.supportsInstallPrompt,
    title: 'Install Polybank',
    instructions: platform.supportsInstallPrompt
      ? ['Click "Install" below or use the install icon in the address bar']
      : ['Use your browser menu to install this app'],
    icon: '💻',
  };
}
